/**
 * accordion.js — standalone <product-accordion> custom element.
 *
 * Loaded by sections that use accordions but do not include product.js
 * (e.g. product-faq, standalone FAQ pages). On PDP pages product.js already
 * defines product-accordion; the guard below prevents double registration.
 *
 * Shared markup contract (identical to the PDP collapsible_row block):
 *   <product-accordion [data-exclusive]>
 *     [<div class="accordion__item">]   <!-- optional wrapper -->
 *       <button data-accordion-trigger
 *               aria-expanded="false"
 *               aria-controls="PANEL-ID">
 *         <span>Heading</span>
 *         <span class="accordion__icon" aria-hidden="true">{chevron svg}</span>
 *       </button>
 *       <div id="PANEL-ID" hidden>Body content</div>
 *     [</div>]
 *   </product-accordion>
 */
(function () {
  class ProductAccordionElement extends HTMLElement {
    connectedCallback() {
      this._exclusive = this.hasAttribute('data-exclusive');
      this.addEventListener('click', this._onClick.bind(this));

      this.querySelectorAll('[data-accordion-trigger]').forEach(trigger => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        const bodyId   = trigger.getAttribute('aria-controls');
        const body     = bodyId ? document.getElementById(bodyId) : trigger.nextElementSibling;
        if (body) {
          body.removeAttribute('hidden');
          body.style.overflow = 'hidden';
          body.style.maxHeight = expanded ? body.scrollHeight + 'px' : '0px';
        }
      });
    }

    _onClick(e) {
      const trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const bodyId   = trigger.getAttribute('aria-controls');
      const body     = bodyId ? document.getElementById(bodyId) : trigger.nextElementSibling;

      if (this._exclusive && !expanded) {
        this.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]').forEach(other => {
          if (other !== trigger) this._close(other);
        });
      }

      expanded ? this._close(trigger) : this._open(trigger, body);
    }

    _open(trigger, body) {
      if (!body) return;
      trigger.setAttribute('aria-expanded', 'true');
      body.style.overflow = 'hidden';
      body.style.maxHeight = body.scrollHeight + 'px';
      body.addEventListener('transitionend', () => {
        body.style.maxHeight = 'none';
        body.style.overflow  = '';
      }, { once: true });
    }

    _close(trigger) {
      const bodyId = trigger.getAttribute('aria-controls');
      const body   = bodyId ? document.getElementById(bodyId) : trigger.nextElementSibling;
      if (!body) return;
      trigger.setAttribute('aria-expanded', 'false');
      body.style.maxHeight = body.scrollHeight + 'px';
      body.style.overflow  = 'hidden';
      body.getBoundingClientRect();
      body.style.maxHeight = '0px';
    }
  }

  if (window.customElements && !window.customElements.get('product-accordion')) {
    window.customElements.define('product-accordion', ProductAccordionElement);
  }
})();
